import React from "react";
import { Box, Stack, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { monoFamily, tokens, radius } from "../../theme";
import { toneColors } from "../statusTone";

import { pressable } from "../pressableProps";
/**
 * One leave type's movement across a period, read left to right the way a
 * statement is: what it opened at, what came in, what went out, what it closed
 * at. Credits and debits are coloured consistently so the direction of a number
 * is legible before the label is read.
 *
 * Opening and closing are null when the ledger holds nothing before the period.
 * That renders as "—", never as 0 — a zero would assert the person started with
 * nothing, which is a different claim from not knowing.
 */

const num = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}));

const Step = ({ label, value, sign, muted }) => {
  const isCredit = sign === "+";
  const isDebit = sign === "-";
  const colour = !value
    ? tokens.textMuted
    : isCredit
    ? toneColors("success").fg
    : isDebit
    ? toneColors("danger").fg
    : tokens.text;

  return (
    <Box sx={{ minWidth: "5rem" }}>
      <Box
        sx={{
          fontSize: "1rem",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          color: muted ? tokens.border : colour,
        }}
      >
        {sign && value ? sign : ""}
        {num(value)}
      </Box>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, whiteSpace: "nowrap" }}>
        {label}
      </Box>
    </Box>
  );
};

const Arrow = () => (
  <Box aria-hidden sx={{ color: tokens.textPlaceholder, fontSize: "1rem", alignSelf: "center", px: 0.5 }}>
    →
  </Box>
);

const StatementCard = ({ statement, active, onClick }) => {
  const t = statement;
  const code = t.leaveType.leaveCode || t.leaveType.name;
  const clickable = Boolean(onClick);
  // a balance the ledger cannot account for at all
  const unexplained = !t.openingKnown && t.ledgerAllTime === 0 && t.currentBalance > 0;
  const drifted = t.reconciles === false;

  return (
    <Box
      component={clickable ? "button" : "div"}
      type={clickable ? "button" : undefined}
      {...pressable(onClick)}
      aria-pressed={clickable ? active : undefined}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        font: "inherit",
        background: tokens.card,
        border: `1px solid ${active ? tokens.accent : tokens.border}`,
        borderRadius: `${radius.card}px`,
        p: "1rem 1.25rem",
        cursor: clickable ? "pointer" : "default",
        transition: "border-color 120ms cubic-bezier(0.2, 0, 0.2, 1)",
        "&:hover": clickable ? { borderColor: tokens.accent } : undefined,
        "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: 1.5, gap: 1, flexWrap: "wrap" }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>{t.leaveType.name}</Box>
          <Box
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: toneColors("muted").fg,
              background: toneColors("muted").bg,
              fontFamily: monoFamily,
              borderRadius: "6px",
              px: 0.5,
              lineHeight: 1.5,
            }}
          >
            {code}
          </Box>
        </Stack>
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Balance now</Box>
          <Box
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: tokens.text,
            }}
          >
            {num(t.currentBalance)}
          </Box>
        </Stack>
      </Stack>

      <Stack direction="row" sx={{ gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Step label="Opening" value={t.opening} muted={!t.openingKnown} />
        <Arrow />
        <Step label="Accrued" value={t.accrued} sign="+" />
        <Step label="Taken" value={t.taken} sign="-" />
        <Step label="Encashed" value={t.encashed} sign="-" />
        <Step label="Adjusted" value={t.adjusted} sign={t.adjusted < 0 ? "-" : "+"} />
        <Arrow />
        <Step label="Closing" value={t.closing} muted={!t.openingKnown} />
      </Stack>

      {(unexplained || drifted) && (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: "0.9375rem", color: tokens.warningText }} />
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
            {drifted ? (
              <>
                Ledger totals {num(t.ledgerAllTime)} but the balance reads {num(t.currentBalance)} —
                something changed this balance without recording a movement.
              </>
            ) : (
              <>This balance predates the ledger, so its opening position is unknown.</>
            )}
          </Box>
          <Tooltip
            arrow
            title="Every credit and debit is written through a single path, so the ledger and the balance should always agree."
          >
            <Box component="span" sx={{ display: "inline-flex", color: tokens.textMuted, cursor: "help" }}>
              <InfoOutlinedIcon sx={{ fontSize: "0.875rem" }} />
            </Box>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
};

export default StatementCard;
